import { Component, OnDestroy, Input, Output, EventEmitter, OnInit, Injector } from '@angular/core';
import { AbstractField } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/abstract-field';
import { Subject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { LocationElements } from '@ordersnproducts/modules/order-builder/modules/location/configs/location-element';
import { IContact, Order } from '@cemex-core/cmx-api';
import { DropdownOption } from '@ordersnproducts/modules/shared/models/dropdownOption/dropdownOptions';
import { NgrxFormControl } from '@ordersnproducts/modules/shared/models/ngrx-form-control/ngrx-form-control';
import { Store } from '@ngrx/store';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { IReceivingPersonsState } from '@ordersnproducts/modules/shared/modules/receiving-person/redux/reducers/receiving-person.reducer';
import {
  OneClickSetOrderReceivingPersonNameAction,
  OneClickSetOrderReceivingPersonPhoneAction,
  OneClickUpdateOrderAction,
} from '@ordersnproducts/modules/one-click-order/actions/one-click-orders.actions';
import { SelectOptionLoaderI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-loader-i';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { FieldSelectConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/field-select-config.interface';
import { getOneClickReceivingPersons } from '@ordersnproducts/modules/one-click-order/selectors/one-click-receiving-person.selector';
import { getOneClickSelectedOrder } from '@ordersnproducts/modules/one-click-order/selectors/one-click-orders.selector';
import { ISelectedReceivingPerson } from '@ordersnproducts/modules/shared/models/selected-receiving-person.interface';
import { FormControlMessageError } from '@ordersnproducts/utils/formControlStatusMessage';

@Component({
  selector: 'app-receiving-person',
  templateUrl: './receiving-person.component.html',
  styleUrls: ['./receiving-person.component.scss'],
})
export class ReceivingPersonComponent extends AbstractField implements OnInit, OnDestroy {
  @Input()
  locationPageFormGroup: UntypedFormGroup;
  @Input()
  parentFormGroup: UntypedFormGroup;
  @Input()
  cancelable = true;
  @Output()
  cancel = new EventEmitter();
  public locationElements = LocationElements;
  public disabled: boolean;
  public required: boolean;
  public showCreatePersonForm: boolean;
  public createPersonCancelable = true;
  public lastSelected: IContact | null = null;
  public dropdownOptions: DropdownOption[];
  public creatingNewContact = false;
  public receivingPersonName: string;
  public receivingPersonPhone: string;
  public options: SelectOptionI[];
  public config: FieldSelectConfig;
  private _destroyed$ = new Subject();
  private persons: IContact[];
  public formControl: UntypedFormControl;
  private optionsLoader: SelectOptionLoaderI;
  private selectedReceivingPerson: ISelectedReceivingPerson;

  ngOnInit(): void {
    this.selectedReceivingPerson = {};
    this.options = this.config.options;
    this.formControl = this.getControl() as UntypedFormControl;
    if (this.config.optionsLoader !== undefined) {
      this.optionsLoader = this.injector.get<SelectOptionLoaderI>(this.config.optionsLoader);
      this.optionsLoader
        .loadOptions(this.group)
        .pipe(takeUntil(this._destroyed$))
        .subscribe((options) => {
          this.options = options;
          if (this.config.undefinedOptionAvailable) {
            this.options.length && this.options.unshift({ id: undefined, name: undefined });
          }
        });
    }
  }

  get control(): NgrxFormControl {
    return this.locationPageFormGroup.controls[LocationElements.RECEIVING_PERSON] as NgrxFormControl;
  }

  constructor(
    private store: Store<OrderBuilderState>,
    private injector: Injector,
    public formControlMessageError: FormControlMessageError,
  ) {
    super();
  }

  public setValue(receivingPersonState: IReceivingPersonsState): void {
    if (this.control.value && !receivingPersonState.selected) {
      this.control.setValue(null);
      return;
    }
    if (
      receivingPersonState.selected &&
      receivingPersonState.selected.contactId &&
      receivingPersonState.selected.contactId !== this.control.value &&
      this.persons.length > 0 &&
      this.persons.some((person) => person.contactId === receivingPersonState.selected.contactId)
    ) {
      this.control.setValue(receivingPersonState.selected.contactId, { emitEvent: false });
      return;
    }
  }

  public toggleCreatingNewContact() {
    this.creatingNewContact = !this.creatingNewContact;
  }

  public contactNameChanged(value) {
    if (value) {
      this.store.dispatch(new OneClickSetOrderReceivingPersonNameAction(value));
    }
    this.formControl.patchValue(value);
    this.selectedReceivingPerson.newName = value;
    this.selectedReceivingPerson.existing = false;
    this.updateControlValue();
  }

  public contactPhoneChanged(value) {
    if (value) {
      this.store.dispatch(new OneClickSetOrderReceivingPersonPhoneAction(value));
    }
    this.formControl.patchValue(value);
    this.selectedReceivingPerson.newPhone = value;
    this.selectedReceivingPerson.existing = false;
    this.updateControlValue();
  }

  public contactSelectCHanged(value) {
    this.store
      .select(getOneClickReceivingPersons)
      .pipe(
        filter((receivingPersons) => !!receivingPersons.length),
        take(1),
        map((receivingPersons) => receivingPersons.find((item) => item.contactId === value)),
        switchMap((receivingPerson) =>
          this.store.select(getOneClickSelectedOrder).pipe(
            take(1),
            map((order) => ({ order, receivingPerson })),
          ),
        ),
      )
      .subscribe(({ order, receivingPerson }) => {
        const modifiedOrder: Order = {
          ...order,
          contact: {
            contactName: receivingPerson.name,
            contactPhone: receivingPerson.phone,
            contactId: receivingPerson.contactId,
            contactCode: receivingPerson.contactCode,
          },
        };
        this.selectedReceivingPerson.selectedName = receivingPerson.name;
        this.selectedReceivingPerson.selectedPhone = receivingPerson.phone;
        this.selectedReceivingPerson.existing = true;
        this.updateControlValue();
        this.store.dispatch(new OneClickUpdateOrderAction(modifiedOrder));
      });
  }

  updateControlValue() {
    this.formControl.setValue(this.selectedReceivingPerson);
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
